/**
 * Application-wide constants. Ported from the legacy `constants.js`,
 * trimmed of values that disappeared with the layered rewrite (server
 * proxy, retry counts, hardcoded defaults that now live on the BFF).
 */

// ── Country codes (subset we actively branch on in UI text) ─────────────

export const COUNTRY_CODES = {
  AU: 'AU',
  NZ: 'NZ',
  US: 'US',
  UK: 'GB',
  CA: 'CA',
} as const;

export type CountryCode = (typeof COUNTRY_CODES)[keyof typeof COUNTRY_CODES];

// ── Payment types ───────────────────────────────────────────────────────

export const PAYMENT_TYPES = {
  RECURRING: 'recurring',
  ONETIME: 'onetime',
} as const;

export type PaymentType = (typeof PAYMENT_TYPES)[keyof typeof PAYMENT_TYPES];

// ── Payment flow timing ─────────────────────────────────────────────────

export const PAYMENT_CONFIG = {
  /** ms — wait before navigating to the summary after a successful pay. */
  SUCCESS_REDIRECT_DELAY: 2000,
  /** Used by the Adyen Drop-in `returnUrl` when 3DS / redirect flows kick in. */
  RETURN_URL: 'https://www.fisherpaykel.com/success',
} as const;

// ── F&P palette (mirrored into MUI theme + Adyen SecuredFields styles) ──

export const UI_CONFIG = {
  LOADING_SPINNER_COLOR: '#a048b6',
  PRIMARY_COLOR: '#A11F7E',
  PRIMARY_HOVER_COLOR: '#a23691',
  DISABLED_BACKGROUND: '#1F1F1F',
  DISABLED_COLOR: 'rgb(228 228 228 / 26%)',
  ERROR_COLOR: '#E93D3D',
  TEXT_COLOR: '#979797',
  SECONDARY_TEXT_COLOR: '#A6A6A6',
} as const;

// ── Adyen SecuredFields styling (passed to AdyenCheckout factory) ───────

export const ADYEN_CONFIG = {
  SUPPORTED_BRANDS: ['mc', 'visa', 'amex', 'bcmc', 'maestro'] as const,
  LOCALE: 'en-US',
  STYLE_CONFIG: {
    base: {
      color: '#fff',
      fontSize: '16px',
      fontSmoothing: 'antialiased',
      fontFamily: 'Helvetica',
      display: 'inline-block',
      background: 'none',
    },
    error: { color: '#E93D3D' },
    validated: { color: 'white' },
    placeholder: { color: 'white' },
  },
} as const;

// ── Copy decks ──────────────────────────────────────────────────────────

export const ERROR_MESSAGES = {
  CARD_NUMBER: 'Insert an available credit card',
  EXPIRY_DATE: 'Select Date',
  GENERAL: 'Something went wrong. Please try refreshing the page.',
  PAYMENT_REFUSED: 'The payment was refused. Please try a different payment method or card.',
  INTERNAL_ERROR: 'Internal error',
  CONFIG_MISSING: 'Configuration error: missing environment variables.',
  TOKEN_INVALID: 'Your session has expired. Please refresh and try again.',
  NETWORK: 'We could not reach the payment service. Check your connection and try again.',
  /**
   * Shown when the shopper cancels the 3DS2 challenge OR enters an incorrect
   * one-time passcode/password (Adyen reports both as `Refused` with
   * refusalReason `3D Not Authenticated`, or `Cancelled`). Without this the
   * card form silently reappeared with no explanation of why the previous
   * attempt didn't go through.
   */
  THREE_DS_NOT_COMPLETED:
    'We could not verify your card. Please re-enter your details and try again.',
  /** Shown when the Adyen SDK reports a hard error (iframe/network) mid-submit. */
  PAYMENT_FORM_ERROR: 'Something went wrong loading the payment form. Please try again.',
} as const;

export const SUCCESS_MESSAGES = {
  ORDER_RECEIVED: 'Your order has been received! Payment completion pending.',
  ORDER_SUCCESS: 'Your order has been successfully placed.',
  PAYMENT_SUCCESS:
    'Your payment has been processed successfully. You will receive a confirmation email shortly.',
  SAVE_CARD_SUCCESS: 'Your card has been saved.',
  THANK_YOU: 'Thank you for choosing Fisher & Paykel Appliances.',
} as const;

export const TERMS_CONTENT = {
  DEFAULT: {
    ACCEPTANCE_TEXT: 'I accept all the terms of the Fisher & Paykel',
    TERMS_LINK_TEXT: 'Terms and Conditions',
    PRIVACY_LINK_TEXT: 'Privacy & Collection Notice',
    AND_TEXT: 'and',
  },
  CA: {
    ACCEPTANCE_TEXT: 'I accept all the terms of the Fisher&Paykel:',
    READ_IN_ENGLISH: 'READ IN ENGLISH',
    READ_IN_FRENCH: 'LIRE EN FRANÇAIS',
    TERMS_LINK_TEXT_EN: 'Terms and Conditions',
    PRIVACY_LINK_TEXT_EN: 'Privacy & Collection Notice',
    TERMS_LINK_TEXT_FR: "Conditions générales d'utilisation",
    PRIVACY_LINK_TEXT_FR: 'Règles de confidentialité',
    AND_TEXT_EN: 'and',
    AND_TEXT_FR: 'et',
  },
} as const;

export const SUBSCRIPTION_CONTENT = {
  AGREEMENT_TEXT: 'I agree with the',
  SUBSCRIPTION_TERMS_TEXT: 'Subscription Terms.',
  CONTINUES_TEXT: 'Subscription continues until you cancel the plan.',
  FOOTNOTE_TITLE: 'How to cancel?',
  FOOTNOTE_DESCRIPTION:
    'By continuing with your payment, you confirm that your subscription will automatically renew and your credit card will automatically be charged the subscription price on this page until you cancel. You also authorise Fisher and Paykel to charge the supplied payment method, or another payment method on file, for the subscription price on each renewal. You can cancel your subscription at any time.',
  CANCEL_INSTRUCTIONS_PREFIX: 'You can',
  CANCEL_INSTRUCTIONS_SUFFIX:
    "anytime by going to the 'My Subscriptions' section and using 'Cancel' option on the active subscription plan.",
  MODIFY_TEXT: 'Modify',
  CANCEL_TEXT: 'Cancel',
  AGREEMENT_TEXT_FR: "J'accepte les",
  SUBSCRIPTION_TERMS_TEXT_FR: "Conditions d'Abonnement.",
  CONTINUES_TEXT_FR: "L'abonnement se poursuivra jusqu'à ce que vous l'annuliez.",
  FOOTNOTE_TITLE_FR: 'Comment annuler?',
  FOOTNOTE_DESCRIPTION_FR:
    "En poursuivant votre paiement, vous confirmez que votre abonnement sera automatiquement renouvelé et que votre carte de crédit sera automatiquement débitée au prix de l'abonnement indiqué sur cette page jusqu'à ce que vous l'annuliez. Vous autorisez également Fisher and Paykel à débiter ce montant via le mode de paiement fourni, ou un autre mode de paiement enregistré. Vous pouvez annuler votre abonnement à tout moment.",
  CANCEL_INSTRUCTIONS_PREFIX_FR: 'Vous pouvez',
  CANCEL_INSTRUCTIONS_SUFFIX_FR:
    "à tout moment votre abonnement en vous rendant dans la section 'Mes Abonnements' et en utilisant l'option 'Résilier' sur le plan d'abonnement actif.",
  MODIFY_TEXT_FR: 'modifier',
  CANCEL_TEXT_FR: 'annuler',
} as const;
